import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getBulkOrderById } from "../../api/BulkorderService";
import {
  FaBuilding,
  FaCalendarAlt,
  FaClipboardList,
  FaInfoCircle,
  FaStickyNote,
} from "react-icons/fa";
import { Card, CardBody, CardHeader } from "../../common/Card";
import { Badge } from "../../common/Feedback";
import RecordActions from "../Shared/RecordActions";
import FullPageLoader from "../../common/FullPageLoader";

export default function BulkorderView() {
  const location = useLocation();
  const navigate = useNavigate();

  const id = location?.state?.bulkOrderId;

  const [bulkOrder, setBulkOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";

    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    if (!id) {
      navigate("/bulk-order");
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const res = await getBulkOrderById(id);
        setBulkOrder(res?.data || res);
      } catch (err) {
        setBulkOrder(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  if (loading) {
    return <FullPageLoader text="Loading bulk order..." />;
  }

  if (!bulkOrder) {
    return <div className="container-fluid p-4"><h5>Bulk order not found</h5></div>;
  }

  return (
    <div className="container-fluid p-4 np-order-view-page">
      <RecordActions onBack={() => navigate("/bulk-order")} />
      <div className="np-order-detail-grid">
        <Section title="Company & Contact Information" icon={FaBuilding}>
          <Row label="Company Name">{bulkOrder.companyName || "-"}</Row>
          <Row label="Contact Person">{bulkOrder.contactPerson || "-"}</Row>
          <Row label="Email">{bulkOrder.email || "-"}</Row>
          <Row label="Phone">{bulkOrder.phoneNo || "-"}</Row>
          <Row label="City">{bulkOrder.city || "-"}</Row>
        </Section>

        <div className="np-order-detail-column">
          <Section title="Project Details" icon={FaClipboardList}>
            <Row label="Project Type">{bulkOrder.projectType || "-"}</Row>
            <Row label="Product Category">{bulkOrder.productCategory || "-"}</Row>
            <Row label="Quantity">{bulkOrder.quantity || "-"}</Row>
            <Row label="Installation Needed">{bulkOrder.isInstallationNeeded ? "Yes" : "No"}</Row>
            <Row label="Required Delivery Date">
              <span className="np-bulk-date">
                <FaCalendarAlt aria-hidden="true" />
                {formatDate(bulkOrder.requiredDeliveryDate)}
              </span>
            </Row>
            <Row label="Agreed">{bulkOrder.isAgreed ? "Yes" : "No"}</Row>
          </Section>

          <Section title="Status" icon={FaInfoCircle}>
            <Row label="Status">
              <Badge tone={getStatusTone(bulkOrder.status)}>{bulkOrder.status || "-"}</Badge>
            </Row>
          </Section>
        </div>

        <Section title="Project Notes" icon={FaStickyNote} className="np-bulk-notes">
          <div className="np-bulk-note">
            <span className="np-order-detail-label">About Project :</span>
            <p className="np-bulk-note__text">{bulkOrder.aboutProject || "-"}</p>
          </div>
          <div className="np-bulk-note">
            <span className="np-order-detail-label">Message :</span>
            <p className="np-bulk-note__text">{bulkOrder.message || "-"}</p>
          </div>
          <div className="np-bulk-note">
            <span className="np-order-detail-label">Reference Design :</span>
            {bulkOrder.referenceDesign?.length > 0 ? (
              <div className="np-bulk-reference-grid">
                {bulkOrder.referenceDesign.map((img, index) => (
                  <img key={index} src={img} alt={`Reference design ${index + 1}`} className="np-bulk-reference-img" />
                ))}
              </div>
            ) : (
              <p className="np-bulk-note__empty">No reference design provided</p>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

const getStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (["APPROVED", "CONFIRMED", "COMPLETED", "ACCEPTED"].some((v) => s.includes(v))) return "success";
  if (["PENDING", "NEW", "REVIEW"].some((v) => s.includes(v))) return "warning";
  if (["PROCESSING", "IN_PROGRESS", "CONTACTED"].some((v) => s.includes(v))) return "info";
  if (["REJECTED", "CANCELLED", "CANCELED", "DECLINED"].some((v) => s.includes(v))) return "danger";
  return "neutral";
};

const Row = ({ label, children }) => (
  <div className="np-order-detail-row">
    <span className="np-order-detail-label">{label} :</span>
    <span className="np-order-detail-value">{children}</span>
  </div>
);

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <Card className={`np-order-section ${className}`.trim()}>
    <CardHeader className="np-order-section__header">
      <span className="np-order-section__icon" aria-hidden="true">
        <Icon />
      </span>
      <h2 className="np-order-section__title">{title}</h2>
    </CardHeader>
    <CardBody className="np-order-section__body">{children}</CardBody>
  </Card>
);
